<script>
    import app from "../stores/app";
    import user from "../stores/user";
    import transfers from "../stores/transfers";
    import {PAYMENT_TYPE, TRANSFER_STATUS, TRANSFERS_SOURCE} from "../lib/constants";
    import {formatDate} from "../lib/lib";
    import {createEventDispatcher} from 'svelte/internal';
    import TableHeader from "../components/TableHeader.svelte";
    import AppIcon from "../components/icons/AppIcon.svelte";

    const tableColumns = [
        {key: 'fullname', title: 'CUSTOMER', sortable: true},
        {key: 'room_no', title: 'ROOM', sortable: true},
        {key: 'flight_boat', title: 'FL/BO', sortable: true},
        {key: 'transfer_at', title: 'DATE', sortable: true, sort_way: 'ASC'},
        {key: 'time', title: 'TIME', sortable: false},
        {key: 'pickup_name', title: 'FROM', sortable: true},
        {key: 'drop_name', title: 'DESTINATION', sortable: true},
        {key: 'group_id', title: 'GR', sortable: false},
        {key: 'drivers', title: 'DRIVER', sortable: false},
        {key: 'vehicles', title: 'VEHICLE', sortable: false},
        {key: 'payment_type', title: 'PT', sortable: true},
        {key: 'price', title: 'PRICE', sortable: true},
        {key: 'passengers', title: 'PAX', sortable: true},
        {key: 'status_id', title: 'ST', sortable: true},
        {key: '', title: '', hidden: !user.canDeleteTransfers()},
    ];

    const paymentTypes = [
        'ACC', 'CSH', 'CPL', 'CC', 'BNK'
    ];

    const transferStatus = [
        {id: 0, icon: '', name: 'Open'},
        {id: 1, icon: 'circle', name: 'Assigned'},
        {id: 2, icon: 'circle-check', name: 'Closed'},
        {id: 3, icon: 'cancel', name: 'Canceled'},
    ];

    const dispatch = createEventDispatcher();

    const onEdit = (transfer_id) => {
        if (user.canEditTransfers()) {
            dispatch("edit", transfer_id);
        } else {
            dispatch("view", transfer_id);
        }
    }

    const onDelete = (transfer) => {
        dispatch("delete", transfer);
    }
</script>

<table class="table table-list table-list-border table-sm mb-4">
    <TableHeader {tableColumns} on:sort/>
    <tbody>
    {#each $transfers.list as transfer}
        <tr class="row-hover" class:status-assigned={transfer.status_id == TRANSFER_STATUS.ASSIGNED}
            class:status-canceled={transfer.status_id == TRANSFER_STATUS.CANCELED}
            class:status-closed={transfer.status_id == TRANSFER_STATUS.CLOSED}
            class:row-selected={$transfers.selectedId === transfer.id}>
            <td class="route-column type-{transfer.route_type}">
                <div class="route-type type-{transfer.route_type}"></div>
                {#if transfer.notified_at != null}
                    <span class="notified" title="notified at {formatDate(transfer.notified_at, 'd-m-y')}">✉</span>
                {/if}
                {#if transfer.source == TRANSFERS_SOURCE.LINK}
                    <span class="source-link" title="customer from link">
                        <AppIcon icon="link" size="12"/>
                    </span>
                {/if}
                <button class="btn-reset is-link text-start w-100 py-1" on:click={ () => onEdit(transfer.id) }>
                    {transfer.customer.fullname}
                </button>
            </td>
            <td class="text-gray">
                {transfer.customer.room_no}
            </td>
            <td class="text-gray">
                {transfer.flight_no}
            </td>
            <td class="transfer-date-column text-gray">
                {formatDate(transfer.transfer_at, 'd-m-y')}
            </td>
            <td class="time-column text-gray">
                {formatDate(transfer.transfer_at, 'H:i')}
            </td>
            <td class="text-gray">
                { transfer.pickup_name }
            </td>
            <td class="text-gray">
                {#if transfer.tour}
                    <div class="tour-name">{transfer.tour.name}</div>
                {:else}
                    {transfer.drop_name}
                {/if}
            </td>
            <td class="button-column">
                {#if transfer.group_id > 0}
                    <button class="btn-reset text-gray p-1" on:click={ () => onEdit(transfer.group_id) }
                            title="open grouped transfer">
                        <AppIcon icon="arrow-right-left-solid"/>
                    </button>
                {/if}
            </td>
            <td>
                {#each transfer.vehicles as item}
                    {#if item.driver}
                        <div class="text-gray">
                            {item.driver.name}
                        </div>
                    {:else}
                        <div class="text-light">
                            no driver
                        </div>
                    {/if}
                {/each}
            </td>
            <td class="vehicle-column">
                {#each transfer.vehicles as item}
                    {#if item.vehicle}
                        <div class="text-gray">
                            {item.vehicle.licence_no}
                        </div>
                    {:else}
                        <div class="text-light">
                            {app.vehicleTypeName(item.vehicle_type_id)}
                        </div>
                    {/if}
                {/each}
            </td>
            <td class="payment-type text-gray" class:payment-bank={transfer.payment_type == PAYMENT_TYPE.BANK}>
                {paymentTypes[transfer.payment_type]}
            </td>
            <td class="text-gray text-end">
                {transfer.price}
            </td>
            <td class="text-gray text-center">
                {transfer.passengers}
            </td>
            <td class="status-column text-gray text-center"
                title={ transferStatus[transfer.status_id].name }>
                <AppIcon icon={ transferStatus[transfer.status_id].icon }/>
            </td>
            {#if user.canDeleteTransfers()}
                <td class="button-column">
                    <button class="btn-reset btn-tool" title="delete transfer"
                            on:click={ () => onDelete(transfer) }>
                        <AppIcon icon="trash" size="16"/>
                    </button>
                </td>
            {/if}
        </tr>
    {/each}
    </tbody>
</table>

<style>
    .route-column {
        position: relative;
        padding-left: 15px !important;
    }

    .route-type {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 5px;
    }

    .route-column.type-0 {
        border-left-color: var(--default-border-color);
    }

    .route-column.type-1 {
        border-left-color: var(--route-arrival-color);
    }

    .route-column.type-2 {
        border-left-color: var(--route-departure-color);
    }

    .route-column.type-3 {
        border-left-color: var(--route-tour-color);
    }

    .route-type.type-0 {
        background: var(--default-border-color);
    }

    .route-type.type-1 {
        background: var(--route-arrival-color);
    }

    .route-type.type-2 {
        background: var(--route-departure-color);
    }

    .route-type.type-3 {
        background: var(--route-tour-color);
    }

    .tour-name {
        color: var(--route-tour-color);
    }

    .transfer-date-column {
        width: 100px;
    }

    .vehicle-column {
        width: 110px;
    }

    .payment-type {
        width: 45px;
    }

    .status-column {
        width: 34px;
    }

    .status-assigned {
        background: var(--status-assigned-background);
    }

    .status-canceled {
        background: var(--status-canceled-background);
    }

    .status-closed {
        background: var(--status-closed-background);
    }

    .source-link {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 15px;
        width: 15px;
        top: 50%;
        left: -6px;
        transform: translateY(-50%);
        background: #12fa04;
        border-radius: 50%;
    }

    .payment-bank {
        background: #12fa04;
    }

    .notified {
        position: absolute;
        top: 0;
        left: -7px;
    }
</style>